/**
 * Chart-spec shapes the Alpinist agent can emit inside a ```chart fenced block.
 * Transport = Option A: the agent writes a JSON spec into a normal markdown text
 * message; the UI parses + renders it (see ChartBlock / MarkdownText). Keep these
 * shapes small and aggregated -- charts are for a handful of points/slices, not
 * raw signal dumps.
 */

/** One (x, y) sample. `x` may be a number, an ISO/date-ish string, or a category label. */
export interface ChartLinePoint {
  x: number | string
  y: number
}

export interface ChartLineSeries {
  name: string
  points: ChartLinePoint[]
  /** Optional explicit line colour (hex, e.g. "#4caf50"); falls back to the palette. */
  color?: string
}

export interface LineChartSpec {
  type: 'line'
  title?: string
  xLabel?: string
  yLabel?: string
  /** Fill under the line(s). */
  area?: boolean
  /** Stack series on top of each other (implies area). Needs a shared x across series. */
  stacked?: boolean
  /** Interpolation. 'step' suits discrete/state signals. */
  curve?: 'monotone' | 'linear' | 'step'
  series: ChartLineSeries[]
}

/** label/value pair for bar + pie. */
export interface CategoryDatum {
  label: string
  value: number
  /** Optional explicit slice/bar colour (hex, e.g. "#4caf50"); falls back to the palette. */
  color?: string
}

/** A named series of category data, for grouped/stacked bars. */
export interface BarSeries {
  name: string
  data: CategoryDatum[]
  /** Optional explicit series colour (hex, e.g. "#4caf50"); falls back to the palette. */
  color?: string
}

export interface BarChartSpec {
  type: 'bar'
  title?: string
  xLabel?: string
  yLabel?: string
  /** Bars run up (vertical, default) or rightward (horizontal -- good for long labels/rankings). */
  orientation?: 'vertical' | 'horizontal'
  /** With multiple series: stack them (true) or cluster side by side (false, default). */
  stacked?: boolean
  /** Single-series shorthand. */
  data?: CategoryDatum[]
  /** Multiple series (grouped or stacked). */
  series?: BarSeries[]
}

export interface PieChartSpec {
  type: 'pie'
  title?: string
  data: CategoryDatum[]
}

/** One KPI in a stat tile. */
export interface StatItem {
  label: string
  value: number | string
  unit?: string
  /** Optional target for context (e.g. "/ 85%"). */
  target?: number
  /** Signed change vs the comparison period. */
  delta?: number
  /** What the delta compares against, e.g. "vs last week". */
  deltaLabel?: string
  /** Colour hint; if omitted no semantic colour is applied. */
  status?: 'good' | 'warn' | 'bad'
}

export interface StatTileSpec extends Partial<StatItem> {
  type: 'stat'
  title?: string
  /** Render several KPIs as a row of tiles; otherwise the top-level value is one tile. */
  items?: StatItem[]
}

export type ChartSpec = LineChartSpec | BarChartSpec | PieChartSpec | StatTileSpec

// Soft size caps. The prompt asks the agent to keep charts small (~50 points / ~12
// slices) and use a table otherwise; these are generous backstops so a non-compliant
// turn fails validation (→ user-facing error, logged) instead of rendering an
// unreadable/slow chart. They also keep array lengths well under the JS
// spread-argument limit (so the Math.min(...arr) usages in the renderers are safe).
const MAX_LINE_POINTS = 1000
const MAX_BAR_CELLS = 500
const MAX_PIE_SLICES = 100
const MAX_STAT_ITEMS = 50

/** Narrow + validate an unknown parsed value into a LineChartSpec. */
export function isLineChartSpec(value: unknown): value is LineChartSpec {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (v.type !== 'line' || !Array.isArray(v.series) || v.series.length === 0) return false
  let totalPoints = 0
  const seriesOk = v.series.every((s) => {
    if (!s || typeof s !== 'object') return false
    const series = s as Record<string, unknown>
    if (typeof series.name !== 'string') return false // interface requires a name
    // A single series may be empty (e.g. a top-N entity with no points in the
    // window); only the chart as a whole must have data (totalPoints > 0 below).
    if (!Array.isArray(series.points)) return false
    totalPoints += series.points.length
    return series.points.every((p) => {
      if (!p || typeof p !== 'object') return false
      const point = p as Record<string, unknown>
      return (
        typeof point.y === 'number' &&
        Number.isFinite(point.y) &&
        (typeof point.x === 'number' || typeof point.x === 'string')
      )
    })
  })
  return seriesOk && totalPoints > 0 && totalPoints <= MAX_LINE_POINTS
}

/** Read the `type` off an unknown parsed spec without trusting its shape. */
export function specType(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const t = (value as Record<string, unknown>).type
  return typeof t === 'string' ? t : undefined
}

/**
 * Entry-level validity for a {label, value}[] array, allowing an *empty* array.
 * Used for individual series of a multi-series bar, where one series may have no
 * points in the window (the chart as a whole still must have data -- enforced by
 * the caller). A single empty series must not invalidate the whole chart.
 */
function isCategoryDatumArray(value: unknown): value is CategoryDatum[] {
  return (
    Array.isArray(value) &&
    value.every((d) => {
      if (!d || typeof d !== 'object') return false
      const datum = d as Record<string, unknown>
      return (
        typeof datum.label === 'string' &&
        typeof datum.value === 'number' &&
        Number.isFinite(datum.value)
      )
    })
  )
}

/** Non-empty {label, value}[] -- required for a single-series bar and for pie. */
function isCategoryData(value: unknown): value is CategoryDatum[] {
  return isCategoryDatumArray(value) && value.length > 0
}

export function isBarChartSpec(value: unknown): value is BarChartSpec {
  if (specType(value) !== 'bar') return false
  const v = value as Record<string, unknown>
  if (isCategoryData(v.data)) return v.data.length <= MAX_BAR_CELLS
  // Multi-series form: a non-empty list of {name, data}. An individual series may
  // be empty (a top-N entity with no points in the window) -- that must not break
  // the whole chart; only require data overall (totalCells > 0 below).
  if (!Array.isArray(v.series) || v.series.length === 0) return false
  let totalCells = 0
  const seriesOk = v.series.every((s) => {
    if (!s || typeof s !== 'object') return false
    const series = s as Record<string, unknown>
    const data = series.data
    if (typeof series.name !== 'string' || !isCategoryDatumArray(data)) return false
    totalCells += data.length
    return true
  })
  return seriesOk && totalCells > 0 && totalCells <= MAX_BAR_CELLS
}

export function isPieChartSpec(value: unknown): value is PieChartSpec {
  if (specType(value) !== 'pie') return false
  // Pie slices must be non-negative (a negative arc is meaningless).
  const data = (value as Record<string, unknown>).data
  return isCategoryData(data) && data.length <= MAX_PIE_SLICES && data.every((d) => d.value >= 0)
}

function isStatValue(v: unknown): v is number | string {
  return (typeof v === 'number' && Number.isFinite(v)) || typeof v === 'string'
}

export function isStatTileSpec(value: unknown): value is StatTileSpec {
  if (specType(value) !== 'stat') return false
  const v = value as Record<string, unknown>
  // Either a list of items, each with a label + value...
  if (Array.isArray(v.items)) {
    return (
      v.items.length > 0 &&
      v.items.length <= MAX_STAT_ITEMS &&
      v.items.every((it) => {
        if (!it || typeof it !== 'object') return false
        const item = it as Record<string, unknown>
        return typeof item.label === 'string' && isStatValue(item.value)
      })
    )
  }
  // ...or a single top-level value.
  return isStatValue(v.value)
}
